/**
 * Save / Cancel / Delete controls for rack editor — toolbar (desktop) or stacked (mobile).
 */

import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { Trash2, Check } from '../icons';
import { colors, spacing } from '../../theme';
import { FormCompactPrimaryButton } from '../forms/FormCompactPrimaryButton';
import {
  RACK_EDIT_DELETE_LABEL,
  RACK_EDIT_SAVE_LABEL,
} from '../../constants/rackConfigurationsSubsectionCopy';

export type RackEditFormActionsVariant = 'toolbar' | 'stacked';

export interface RackEditFormActionsProps {
  testID: string;
  variant: RackEditFormActionsVariant;
  isNew: boolean;
  canSave: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  isFormDisabled: boolean;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  saveLabel?: string;
  deleteLabel?: string;
}

export const RackEditFormActions: React.FC<RackEditFormActionsProps> = ({
  testID,
  variant,
  isNew,
  canSave,
  isSaving,
  isDeleting,
  isFormDisabled,
  onSave,
  onCancel,
  onDelete,
  saveLabel = RACK_EDIT_SAVE_LABEL,
  deleteLabel = RACK_EDIT_DELETE_LABEL,
}) => {
  const showDelete = !isNew && Boolean(onDelete);

  if (variant === 'stacked') {
    return (
      <View style={styles.stackedWrap} testID={`${testID}-actions-stacked`}>
        <FormCompactPrimaryButton
          testID={`${testID}-save-button`}
          onPress={onSave}
          disabled={!canSave}
          accessibilityLabel={saveLabel}
          icon={
            isSaving ? (
              <ActivityIndicator size="small" color={colors.textLight} />
            ) : (
              <Check size={16} color={colors.textLight} />
            )
          }
          style={styles.stackedPrimaryButton}
        >
          {saveLabel}
        </FormCompactPrimaryButton>

        <TouchableOpacity
          testID={`${testID}-cancel-button`}
          style={styles.stackedSecondaryButton}
          onPress={onCancel}
          accessibilityLabel="Cancel"
          accessibilityRole="button"
        >
          <Text style={styles.stackedSecondaryButtonText}>Cancel</Text>
        </TouchableOpacity>

        {showDelete ? (
          <TouchableOpacity
            testID={`${testID}-delete-button`}
            style={[
              styles.stackedSecondaryButton,
              styles.stackedDeleteButton,
              isFormDisabled && styles.buttonDisabled,
            ]}
            onPress={onDelete}
            disabled={isFormDisabled}
            accessibilityLabel={deleteLabel}
            accessibilityRole="button"
          >
            {isDeleting ? (
              <ActivityIndicator size="small" color={colors.error} />
            ) : (
              <>
                <Trash2 size={18} color={colors.error} />
                <Text style={styles.stackedDeleteButtonText}>{deleteLabel}</Text>
              </>
            )}
          </TouchableOpacity>
        ) : null}
      </View>
    );
  }

  return (
    <View style={styles.toolbar} testID={`${testID}-actions-toolbar`}>
      <View style={styles.toolbarLeft}>
        {showDelete ? (
          <TouchableOpacity
            testID={`${testID}-delete-button`}
            style={[styles.deleteButton, isFormDisabled && styles.buttonDisabled]}
            onPress={onDelete}
            disabled={isFormDisabled}
            accessibilityLabel={deleteLabel}
            accessibilityRole="button"
          >
            {isDeleting ? (
              <ActivityIndicator size="small" color={colors.error} />
            ) : (
              <>
                <Trash2 size={18} color={colors.error} />
                <Text style={styles.deleteButtonText}>{deleteLabel}</Text>
              </>
            )}
          </TouchableOpacity>
        ) : null}
      </View>

      <View style={styles.toolbarRight}>
        <TouchableOpacity
          testID={`${testID}-cancel-button`}
          style={styles.cancelButton}
          onPress={onCancel}
          accessibilityLabel="Cancel"
          accessibilityRole="button"
        >
          <Text style={styles.cancelButtonText}>Cancel</Text>
        </TouchableOpacity>

        <TouchableOpacity
          testID={`${testID}-save-button`}
          style={[styles.saveButton, !canSave && styles.saveButtonDisabled]}
          onPress={onSave}
          disabled={!canSave}
          accessibilityLabel={saveLabel}
          accessibilityRole="button"
        >
          {isSaving ? (
            <ActivityIndicator size="small" color={colors.textLight} />
          ) : (
            <>
              <Check size={16} color={colors.textLight} style={styles.saveButtonIcon} />
              <Text style={[styles.saveButtonText, !canSave && styles.saveButtonTextDisabled]}>
                {saveLabel}
              </Text>
            </>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
};

const stackedButtonBase = {
  width: '100%' as const,
  minHeight: 48,
  flexDirection: 'row' as const,
  alignItems: 'center' as const,
  justifyContent: 'center' as const,
  borderRadius: 8,
  paddingVertical: spacing.md,
  paddingHorizontal: spacing.md,
  gap: spacing.sm,
};

const styles = StyleSheet.create({
  stackedWrap: {
    width: '100%',
    marginTop: spacing.lg,
    gap: spacing.sm,
  },
  stackedPrimaryButton: {
    alignSelf: 'stretch',
    width: '100%',
    justifyContent: 'center',
    minHeight: 48,
  },
  stackedSecondaryButton: {
    ...stackedButtonBase,
    borderWidth: 1,
    borderColor: colors.borderCard,
    backgroundColor: colors.backgroundPrimary,
    ...Platform.select({
      web: {
        cursor: 'pointer' as unknown as undefined,
      },
    }),
  },
  stackedSecondaryButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.textPrimary,
  },
  stackedDeleteButton: {
    borderColor: colors.error,
  },
  stackedDeleteButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.error,
  },
  toolbar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.md,
    width: '100%',
  },
  toolbarLeft: {
    flex: 1,
    minWidth: 0,
  },
  toolbarRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flexShrink: 0,
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.error,
    gap: 6,
    alignSelf: 'flex-start',
  },
  deleteButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.error,
  },
  cancelButton: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.borderCard,
    backgroundColor: colors.backgroundPrimary,
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.textPrimary,
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: 8,
    backgroundColor: colors.primary,
    minWidth: 130,
  },
  saveButtonDisabled: {
    backgroundColor: colors.borderCard,
  },
  saveButtonIcon: {
    marginRight: 6,
  },
  saveButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.textLight,
  },
  saveButtonTextDisabled: {
    color: colors.textMuted,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
});
